import React, { useState } from 'react';
import { AcquisitionProfile } from '../../types';
import { apiClient } from '../../api/client';

function ReviewBar({ profile }: { profile: AcquisitionProfile }) {
  const [checked, setChecked] = useState<boolean>(profile.profile_checked ?? true);
  const [approved, setApproved] = useState<boolean>(profile.profile_approved ?? true);
  const [busy, setBusy] = useState<'check' | 'approve' | null>(null);

  async function patch(field: 'checked' | 'approved', value: boolean) {
    setBusy(field === 'checked' ? 'check' : 'approve');
    try {
      await apiClient.patch(`/research/profiles/${profile.id}/review`, { [field]: value });
      if (field === 'checked') setChecked(value);
      else setApproved(value);
    } catch {
      // bei Fehler revert
      if (field === 'checked') setChecked(!value);
      else setApproved(!value);
    } finally {
      setBusy(null);
    }
  }

  const reviewNeeded = !approved;

  // stopPropagation: Klicks auf Review-Bar dürfen NICHT das Profil-Modal öffnen
  const stop = (e: React.MouseEvent | React.ChangeEvent) => e.stopPropagation();

  return (
    <div
      onClick={stop}
      style={{
        display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap',
        padding: '6px 10px', marginBottom: 10,
        background: reviewNeeded ? 'var(--warn-bg)' : 'var(--success-bg)',
        border: `1px solid ${reviewNeeded ? 'var(--warn)' : 'var(--success)'}`,
        borderRadius: 'var(--radius)',
        fontSize: 12,
      }}
    >
      {reviewNeeded && (
        <span style={{
          padding: '2px 8px', background: 'var(--warn)', color: '#fff',
          borderRadius: 12, fontSize: 11, fontWeight: 600,
        }}>Review nötig</span>
      )}
      <label
        onClick={stop}
        style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
      >
        <input
          type="checkbox" checked={checked} disabled={busy !== null}
          onClick={stop}
          onChange={e => { e.stopPropagation(); patch('checked', e.target.checked); }}
        />
        Geprüft
      </label>
      <label
        onClick={stop}
        style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}
      >
        <input
          type="checkbox" checked={approved} disabled={busy !== null}
          onClick={stop}
          onChange={e => { e.stopPropagation(); patch('approved', e.target.checked); }}
        />
        Freigegeben
      </label>
      {profile.source === 'web_extraction' && profile.source_url && (
        <a href={profile.source_url} target="_blank" rel="noopener noreferrer"
           onClick={stop}
           style={{ color: 'var(--text3)', fontSize: 11, marginLeft: 'auto' }}>
          Quelle: {new URL(profile.source_url).hostname}
        </a>
      )}
    </div>
  );
}

function formatVol(v?: number | null): string {
  if (!v) return '';
  if (v >= 1_000_000_000) return `${(v / 1_000_000_000).toFixed(1)} Mrd.`;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(0)} Mio.`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(0)} Tsd.`;
  return `${v}`;
}

function ConfidenceBadge({ value }: { value?: number | null }) {
  if (!value) return null;
  const pct = Math.round(value * 100);
  const color = pct >= 80 ? 'var(--success)' : pct >= 60 ? 'var(--warn)' : 'var(--accent)';
  const bg   = pct >= 80 ? 'var(--success-bg)' : pct >= 60 ? 'var(--warn-bg)' : 'var(--accent-light)';
  return (
    <span style={{ padding: '2px 8px', background: bg, color, borderRadius: '20px', fontSize: '11px', fontWeight: 500, fontFamily: 'DM Mono, monospace' }}>
      {pct}% KI
    </span>
  );
}

function Chip({ label, accent }: { label: string; accent?: boolean }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 9px',
      background: accent ? 'var(--accent-light)' : 'var(--surface)',
      color: accent ? 'var(--accent)' : 'var(--text2)',
      border: `1px solid ${accent ? 'var(--accent-mid)' : 'var(--border)'}`,
      borderRadius: '20px', fontSize: '11px', fontWeight: accent ? 500 : 400,
    }}>{label}</span>
  );
}

function GreenChip({ label }: { label: string }) {
  return (
    <span style={{
      display: 'inline-block', padding: '2px 9px',
      background: 'var(--success-bg)', color: 'var(--success)',
      border: '1px solid #b2dfcc', borderRadius: '20px', fontSize: '11px', fontWeight: 500,
    }}>{label}</span>
  );
}

function Tag({ label }: { label: string }) {
  return (
    <span style={{
      fontSize: '11px', color: 'var(--text2)', background: 'var(--surface)',
      border: '1px solid var(--border)', borderRadius: '4px', padding: '1px 6px',
    }}>{label}</span>
  );
}

const RISK_LABEL: Record<string, string> = {
  core: 'Core', core_plus: 'Core+', value_add: 'Value Add', opportunistic: 'Opportunistisch',
};

interface ProfileCardProps { profile: AcquisitionProfile; }

export function ProfileCard({ profile }: ProfileCardProps) {
  const volumeStr = (() => {
    const min = profile.volume_min_eur, max = profile.volume_max_eur;
    if (!min && !max) return null;
    if (min && max) return `${formatVol(min)} – ${formatVol(max)} €`;
    if (min) return `ab ${formatVol(min)} €`;
    return `bis ${formatVol(max)} €`;
  })();

  // single asset_class_label hat Vorrang (single class pro Profil); legacy plural als Fallback
  const assetLabels = profile.asset_class_label
    ? [profile.asset_class_label]
    : (profile.asset_class_labels ?? []).filter(Boolean);
  const exclAssetLabels = (profile.exclusion_asset_class_labels ?? []).filter(Boolean);
  const hasExclusions = exclAssetLabels.length > 0 || (profile.exclusion_object_conditions?.length ?? 0) > 0
    || (profile.exclusion_countries?.length ?? 0) > 0 || (profile.exclusion_cities?.length ?? 0) > 0
    || profile.exclusion_notes;

  return (
    <div className="card" style={{ padding: '16px 20px' }}>
      <ReviewBar profile={profile} />
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', marginBottom: '12px' }}>
        <div>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 4, flexWrap: 'wrap' }}>
            {profile.asset_class_label ? (
              <span style={{
                padding: '5px 12px', borderRadius: 6, fontSize: 13, fontWeight: 700,
                background: 'var(--accent)', color: '#fff', letterSpacing: '0.3px',
              }}>📦 {profile.asset_class_label}</span>
            ) : (
              <span style={{
                padding: '5px 12px', borderRadius: 6, fontSize: 13, fontWeight: 700,
                background: 'var(--warn)', color: '#fff',
              }}>⚠ ohne Asset-Klasse</span>
            )}
            <div style={{ fontWeight: 500, fontSize: 14, color: 'var(--text2)' }}>
              {profile.profile_name ?? 'Ankaufsprofil'}
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--text3)' }}>
            {(() => {
              const src = profile.last_source ?? profile.source ?? 'manual';
              const srcLabel = src === 'web_research' ? 'Web-Recherche' : src === 'upload' || src === 'pdf_import' ? 'PDF-Upload' : 'Manuell';
              const at = profile.last_source_at ?? profile.updated_at ?? profile.created_at;
              return `Zuletzt aktualisiert: ${new Date(at).toLocaleDateString('de-DE')} · ${srcLabel}`;
            })()}
            {profile.source_language && ` · ${profile.source_language.toUpperCase()}`}
          </div>
        </div>
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <ConfidenceBadge value={profile.ai_confidence} />
          {!profile.is_active && (
            <span style={{ padding: '2px 8px', background: 'var(--surface2)', color: 'var(--text3)', borderRadius: '20px', fontSize: '11px' }}>Inaktiv</span>
          )}
        </div>
      </div>

      {/* 3 Dimensionen */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>

        {/* ① Asset */}
        <div style={{ background: 'var(--surface2)', borderRadius: 'var(--radius)', padding: '10px 12px', display: 'grid', gap: '6px', alignContent: 'start' }}>
          <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Asset</div>

          {assetLabels.length > 0 ? (
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {assetLabels.map((l, i) => <GreenChip key={i} label={l} />)}
            </div>
          ) : (
            <span style={{ fontSize: '12px', color: 'var(--text3)' }}>—</span>
          )}

          {profile.object_conditions && profile.object_conditions.length > 0 && (
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {profile.object_conditions.map((c, i) => <Chip key={i} label={c} />)}
            </div>
          )}

          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {profile.min_units != null && <Tag label={`ab ${profile.min_units} Einh.`} />}
            {profile.unit_max_sqm != null && <Tag label={`bis ${profile.unit_max_sqm} m²/WE`} />}
            {profile.unit_min_sqm != null && <Tag label={`ab ${profile.unit_min_sqm} m²/WE`} />}
            {profile.max_year_built != null && <Tag label={`Bj. bis ${profile.max_year_built}`} />}
            {profile.min_year_built != null && <Tag label={`Bj. ab ${profile.min_year_built}`} />}
            {profile.min_size_sqm != null && <Tag label={`ab ${profile.min_size_sqm} m²`} />}
            {profile.max_size_sqm != null && <Tag label={`bis ${profile.max_size_sqm} m²`} />}
          </div>

          {profile.asset_notes && (
            <div style={{ fontSize: '11px', color: 'var(--text2)', lineHeight: '1.4', borderTop: '1px solid var(--border)', paddingTop: '4px' }}>
              {profile.asset_notes.slice(0, 140)}{profile.asset_notes.length > 140 ? '…' : ''}
            </div>
          )}
        </div>

        {/* ② Lage */}
        <div style={{ background: 'var(--surface2)', borderRadius: 'var(--radius)', padding: '10px 12px', display: 'grid', gap: '6px', alignContent: 'start' }}>
          <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Lage</div>

          {profile.target_countries && profile.target_countries.length > 0 ? (
            <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
              {profile.target_countries.map((c, i) => <Chip key={i} label={c} accent />)}
            </div>
          ) : <span style={{ fontSize: '12px', color: 'var(--text3)' }}>—</span>}

          {profile.target_cities && profile.target_cities.length > 0 && (
            <div style={{ fontSize: '12px', fontWeight: 500, color: 'var(--text)' }}>
              {profile.target_cities.join(', ')}
            </div>
          )}

          {profile.target_regions && profile.target_regions.length > 0 && (
            <div style={{ fontSize: '11px', color: 'var(--text2)' }}>
              {profile.target_regions.slice(0, 6).join(', ')}
              {profile.target_regions.length > 6 && ` +${profile.target_regions.length - 6}`}
            </div>
          )}

          {profile.location_description_en && !profile.target_cities?.length && (
            <div style={{ fontSize: '11px', color: 'var(--text2)', lineHeight: '1.4' }}>
              {profile.location_description_en.slice(0, 100)}
            </div>
          )}
        </div>

        {/* ③ Finanzen */}
        <div style={{ background: 'var(--surface2)', borderRadius: 'var(--radius)', padding: '10px 12px', display: 'grid', gap: '6px', alignContent: 'start' }}>
          <div style={{ fontSize: '10px', fontWeight: 600, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.6px' }}>Finanzen</div>

          {volumeStr && (
            <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text)', fontFamily: 'DM Mono, monospace' }}>{volumeStr}</div>
          )}

          <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
            {profile.risk_profile && <Chip label={RISK_LABEL[profile.risk_profile] ?? profile.risk_profile} accent />}
            {profile.niy_min_pct != null && <Chip label={`NIY ${profile.niy_min_pct}${profile.niy_max_pct != null ? `–${profile.niy_max_pct}` : ''}%`} />}
            {profile.factor_min != null && <Chip label={`Faktor ${profile.factor_min}${profile.factor_max != null ? `–${profile.factor_max}` : ''}×`} />}
          </div>

          {!volumeStr && !profile.risk_profile && !profile.niy_min_pct && !profile.factor_min && (
            <span style={{ fontSize: '12px', color: 'var(--text3)' }}>—</span>
          )}

          {profile.financial_exclusions && profile.financial_exclusions.length > 0 && (
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: '4px', marginTop: '2px' }}>
              <div style={{ fontSize: '10px', color: 'var(--text3)', marginBottom: '3px', textTransform: 'uppercase', letterSpacing: '0.4px' }}>Ausschluss</div>
              <div style={{ fontSize: '11px', color: 'var(--text2)' }}>
                {profile.financial_exclusions.slice(0, 3).join(' · ')}
                {profile.financial_exclusions.length > 3 && ` +${profile.financial_exclusions.length - 3}`}
              </div>
            </div>
          )}
        </div>
      </div>
      {/* Ausschlusskriterien */}
      {hasExclusions && (
        <div style={{ marginTop: '10px', border: '1.5px solid var(--accent-mid)', borderRadius: 'var(--radius)', padding: '10px 12px', background: 'var(--accent-light)' }}>
          <div style={{ fontSize: '10px', fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '6px' }}>
            ✕ Ausschlusskriterien
          </div>
          <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
            {exclAssetLabels.map((l, i) => (
              <span key={i} style={{ display: 'inline-block', padding: '2px 9px', background: 'var(--accent)', color: '#fff', borderRadius: '20px', fontSize: '11px', fontWeight: 500 }}>✕ {l}</span>
            ))}
            {profile.exclusion_object_conditions?.map((c, i) => (
              <span key={i} style={{ display: 'inline-block', padding: '2px 9px', background: '#fff', color: 'var(--accent)', border: '1px solid var(--accent-mid)', borderRadius: '20px', fontSize: '11px' }}>✕ {c}</span>
            ))}
            {profile.exclusion_countries?.map((c, i) => (
              <span key={i} style={{ display: 'inline-block', padding: '2px 9px', background: '#fff', color: 'var(--accent)', border: '1px solid var(--accent-mid)', borderRadius: '20px', fontSize: '11px' }}>✕ {c}</span>
            ))}
            {profile.exclusion_cities?.map((c, i) => (
              <span key={i} style={{ display: 'inline-block', padding: '2px 9px', background: '#fff', color: 'var(--accent)', border: '1px solid var(--accent-mid)', borderRadius: '20px', fontSize: '11px' }}>✕ {c}</span>
            ))}
          </div>
          {profile.exclusion_notes && (
            <div style={{ fontSize: '11px', color: 'var(--accent)', marginTop: '5px', lineHeight: '1.4' }}>
              {profile.exclusion_notes}
            </div>
          )}
        </div>
      )}
      {profile.source_document_id && (
        <div style={{ marginTop: '12px', paddingTop: '10px', borderTop: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            type="button"
            onClick={async (e) => {
              e.stopPropagation();
              try {
                const resp = await apiClient.get(`/pdf/documents/${profile.source_document_id}/file`, { responseType: 'blob' });
                const url = URL.createObjectURL(resp.data as Blob);
                window.open(url, '_blank');
                setTimeout(() => URL.revokeObjectURL(url), 10000);
              } catch { /* ignore */ }
            }}
            style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: '12px', color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: '4px' }}
          >
            📄 Quell-PDF öffnen
          </button>
        </div>
      )}
    </div>
  );
}
